import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  indexedDBLocalPersistence,
  setPersistence,
  User,
  UserCredential,
  signInWithEmailAndPassword,
  Auth,
  signInWithRedirect,
  GoogleAuthProvider,
  getRedirectResult,
  signOut,
  onIdTokenChanged,
  onAuthStateChanged,
  beforeAuthStateChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
} from 'firebase/auth'
import * as Sentry from '@sentry/react'
import { getClientTenantAuth } from './getClientTenantAuth'

type AuthUser = {
  id: string
  displayName?: string | null
  email?: string | null
  emailVerified?: boolean
  loading?: boolean
  photoURL?: string | null
  getIdToken: (forceRefresh: boolean) => Promise<string>
}

export interface AuthContextProps {
  user: AuthUser | null
  loading: boolean
  logout: () => Promise<void | null>
  loginWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<UserCredential> | null
  isInitialized: boolean
  loginWithGoogle: () => Promise<void> | null
  isRequestComplete: boolean
  sendResetPassword: (email: string) => Promise<void> | null
  resetPassword: (code: string, newPassword: string) => Promise<void> | null
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

const REFRESH_INTERVAL = 60 * 30 * 1000 // 30 minutes

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<null | AuthUser>(null)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isRequestComplete, setIsRequestComplete] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const firebaseAuth = useRef<null | Auth>(null)
  const refreshInterval = useRef<null | ReturnType<typeof setInterval>>(null)

  useEffect(() => {
    if (!firebaseAuth.current) {
      const auth = getClientTenantAuth()
      firebaseAuth.current = auth

      setPersistence(auth, indexedDBLocalPersistence).catch((error) => {
        Sentry.captureException(error)
      })
      auth?.authStateReady().then(() => {
        setIsInitialized(true)
      })
    }
  }, [])

  useEffect(() => {
    // Subscribe to auth on mount
    const auth = firebaseAuth.current
    if (!auth) {
      return () => {}
    }
    let isCancelled = false

    const unsubscribeBeforeStateChange = beforeAuthStateChanged(
      auth,
      async () => {
        setLoading(true)
      }
    )

    const unsubscribeAuthStateChange = onAuthStateChanged(
      auth,
      async (u: User) => {
        if (u) {
          await getRedirectResult(auth)
        }
      }
    )

    const unsubscribeIdTokenChanged = onIdTokenChanged(
      auth,
      async (u: User) => {
        if (u) {
          if (refreshInterval.current) {
            clearInterval(refreshInterval.current)
          }
          setUser({
            id: u.uid,
            displayName: u?.displayName ?? null,
            email: u?.email,
            emailVerified: u?.emailVerified,
            photoURL: u?.photoURL,
            getIdToken: (forceRefresh: boolean) => u?.getIdToken(forceRefresh),
          })
          refreshInterval.current = setInterval(async () => {
            if (isCancelled) {
              return
            }
            await u?.getIdToken(true)
          }, REFRESH_INTERVAL)
        } else {
          if (refreshInterval.current) {
            clearInterval(refreshInterval.current)
          }
          setUser(null)
        }
        if (!isCancelled) {
          setIsRequestComplete(true)
        }
        setLoading(false)
      }
    )

    // Unsubscribe on cleanup
    return () => {
      unsubscribeBeforeStateChange()
      unsubscribeAuthStateChange()
      unsubscribeIdTokenChanged()
      isCancelled = true
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current)
      }
    }
  }, [])

  const logout = useCallback(async () => {
    const auth = firebaseAuth.current
    if (!auth) {
      return null
    }
    setUser(null)
    return signOut(auth)
  }, [])

  const loginWithEmailAndPassword = useCallback(
    (email: string, password: string) => {
      const auth = firebaseAuth.current
      if (!auth) {
        return null
      }

      return signInWithEmailAndPassword(auth, email, password)
    },
    []
  )

  const loginWithGoogle = useCallback(() => {
    const auth = firebaseAuth.current
    if (!auth) {
      return null
    }
    const provider = new GoogleAuthProvider()
    return signInWithRedirect(auth, provider)
  }, [])

  const sendResetPassword = useCallback((email: string) => {
    const auth = firebaseAuth.current
    if (!auth) {
      return null
    }

    return sendPasswordResetEmail(auth, email)
  }, [])

  const resetPassword = useCallback((code: string, newPassword: string) => {
    const auth = firebaseAuth.current
    if (!auth) {
      return null
    }
    return confirmPasswordReset(auth, code, newPassword)
  }, [])

  const value = useMemo(
    () => ({
      user,
      loading,
      logout,
      loginWithEmailAndPassword,
      loginWithGoogle,
      isInitialized,
      isRequestComplete,
      sendResetPassword,
      resetPassword,
    }),
    [
      user,
      loading,
      logout,
      loginWithEmailAndPassword,
      loginWithGoogle,
      isInitialized,
      isRequestComplete,
      sendResetPassword,
      resetPassword,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useUser = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a AuthProvider')
  }
  return context
}

export { AuthProvider, useUser }
